import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import AnImage from './AnImage';

import './custom-gallery.scss';

const MAX_IMAGES = 10;

const calculateColumnBentoLayout = (count, columnIndex) => {
  if (count === 0) {
    return { subColumns: 1, layouts: [] };
  }

  if (columnIndex === 0) {
    if (count === 1) return { subColumns: 1, layouts: [{ colSpan: 1, rowSpan: 4 }] };
    if (count >= 2) {
      return {
        subColumns: 1,
        layouts: [
          { colSpan: 1, rowSpan: 3 },
          { colSpan: 1, rowSpan: 1 },
        ],
      };
    }
  }

  if (columnIndex === 1) {
    if (count === 1) return { subColumns: 2, layouts: [{ colSpan: 2, rowSpan: 4 }] };
    if (count === 2) {
      return {
        subColumns: 2,
        layouts: [
          { colSpan: 1, rowSpan: 2 },
          { colSpan: 1, rowSpan: 2 },
        ],
      };
    }
    if (count === 3) {
      return {
        subColumns: 2,
        layouts: [
          { colSpan: 2, rowSpan: 2 },
          { colSpan: 1, rowSpan: 2 },
          { colSpan: 1, rowSpan: 2 },
        ],
      };
    }
    return {
      subColumns: 2,
      layouts: [
        { colSpan: 2, rowSpan: 1 },
        { colSpan: 1, rowSpan: 2 },
        { colSpan: 1, rowSpan: 2 },
        { colSpan: 2, rowSpan: 1 },
      ],
    };
  }

  if (count === 1) return { subColumns: 2, layouts: [{ colSpan: 2, rowSpan: 4 }] };
  if (count === 2) {
    return {
      subColumns: 2,
      layouts: [
        { colSpan: 2, rowSpan: 2 },
        { colSpan: 2, rowSpan: 2 },
      ],
    };
  }
  if (count === 3) {
    return {
      subColumns: 2,
      layouts: [
        { colSpan: 1, rowSpan: 2 },
        { colSpan: 1, rowSpan: 2 },
        { colSpan: 2, rowSpan: 2 },
      ],
    };
  }
  return {
    subColumns: 2,
    layouts: [
      { colSpan: 1, rowSpan: 4 },
      { colSpan: 1, rowSpan: 1 },
      { colSpan: 1, rowSpan: 1 },
      { colSpan: 1, rowSpan: 2 },
    ],
  };
};

const isPortrait = (image) => {
  const customClass = String(image.customclass || image.customClass || '').toLowerCase();
  if (customClass.includes('portrait')) return true;
  if (customClass.includes('paysage') || customClass.includes('landscape')) return false;
  if (Number.isFinite(image.width) && Number.isFinite(image.height)) {
    return Number(image.height) > Number(image.width);
  }
  return false;
};

const arrangeImagesForLayouts = (images, layouts) => {
  const pool = [...images];
  const ordered = [];

  layouts.forEach((layout) => {
    if (!pool.length) return;

    const needsPortrait = layout.colSpan === 1 && layout.rowSpan >= 2;
    const targetIndex = pool.findIndex((img) =>
      needsPortrait ? isPortrait(img) : !isPortrait(img)
    );

    if (targetIndex >= 0) {
      ordered.push(pool.splice(targetIndex, 1)[0]);
      return;
    }

    ordered.push(pool.shift());
  });

  return ordered;
};

const distributeImages = (images) => {
  const limited = images.slice(0, MAX_IMAGES);
  const portraits = limited.filter((img) => isPortrait(img));
  const others = limited.filter((img) => !isPortrait(img));

  const column1 = portraits.slice(0, 2);
  const remainingPortraits = portraits.slice(column1.length);
  const remaining = [...remainingPortraits, ...others];

  while (column1.length < 2 && remaining.length) {
    column1.push(remaining.shift());
  }

  const column2 = remaining.slice(0, 4);
  const column3 = remaining.slice(4, 8);

  return { column1, column2, column3, total: limited.length };
};

const BentoColumn = ({ images, columnIndex, allPictures }) => {
  const { subColumns, layouts } = useMemo(
    () => calculateColumnBentoLayout(images.length, columnIndex),
    [images.length, columnIndex]
  );

  const orderedImages = useMemo(
    () => arrangeImagesForLayouts(images, layouts),
    [images, layouts]
  );

  if (!orderedImages.length) {
    return null;
  }

  return (
    <div
      className={`bento-gallery__column-grid bento-gallery__column-grid--${subColumns}`}
    >
      {orderedImages.map((image, index) => {
        const layout = layouts[index] || { colSpan: 1, rowSpan: 1 };
        return (
          <AnImage
            key={image.id || image.key || index}
            photo={image}
            allPictures={allPictures}
            className="bento-gallery__tile"
            style={{
              gridColumn: `span ${layout.colSpan || 1}`,
              gridRow: `span ${layout.rowSpan || 1}`,
            }}
          />
        );
      })}
    </div>
  );
};

const CustomGallery = ({ photos, allPictures }) => {
  const { column1, column2, column3, total } = useMemo(
    () => distributeImages(photos),
    [photos]
  );
  const showColumn3 = total > 6;

  if (total === 0) {
    return <div className="bento-gallery__empty">No pictures to display yet.</div>;
  }

  return (
    <div className="bento-gallery">
      <div className={`bento-gallery__column bento-gallery__column--left${showColumn3 ? '' : ' bento-gallery__column--left-wide'}`}>
        <BentoColumn images={column1} columnIndex={0} allPictures={allPictures} />
      </div>

      <div className={`bento-gallery__column bento-gallery__column--center${showColumn3 ? '' : ' bento-gallery__column--center-wide'}`}>
        <BentoColumn images={column2} columnIndex={1} allPictures={allPictures} />
      </div>

      {showColumn3 ? (
        <div className="bento-gallery__column bento-gallery__column--right">
          <BentoColumn images={column3} columnIndex={2} allPictures={allPictures} />
        </div>
      ) : null}
    </div>
  );
};

CustomGallery.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.object).isRequired,
  allPictures: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CustomGallery;
